import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Zap, Diamond, ChevronDown, Loader2, Check, Plus } from 'lucide-react';
import { usePortalDropdown } from '../usePortalDropdown';
import type { InstantActionDefinition } from '@shared/schemas/instant-action-definition';
import { useEnrollQueue } from '../EnrollQueue/EnrollQueueContext';
import { PlayPicker } from '../EnrollQueue/PlayPicker';
import { Collapsible } from '../../shared/Collapsible';
import { useFireInstantAction, useEnrollContact, READ_ONLY_TITLE } from './useContactActions';

/**
 * Contact card "Actions ▾" + "Queue for Play ▾" dropdowns (flow-workflow-requests
 * U7, requirements R1/R8). Mounted in the contact detail header
 * (`ContactDetailPanels.tsx`) — see prototype
 * `docs/flow/flow-workflow-requests-prototype.html`'s `c-head .cluster`.
 * `WorkflowsDropdown` (the "Queue for Play ▾" half, exported below) is also
 * the single-contact enroll surface reused directly by `ContactFlowTable`'s
 * row action and `SearchResultsSurface` (flow-contacts-bulk-send U7,
 * decision 12) — the standalone `EnrollButton` component this replaced is
 * deleted.
 *
 * Dropdown chrome (open state, outside-click/Escape close, portal geometry)
 * comes from the shared `usePortalDropdown` (Unit 3 dropdown-positioning-
 * logic promotion) — no new dependency (plan decision).
 */

const TRIGGER_CLASS =
  'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800 hover:bg-bg-secondary dark:hover:bg-nightshade-700';

export interface ContactActionMenusProps {
  contactId: string;
  /** Display name for toasts/optimistic feed rows; null renders a generic fallback. */
  contactName: string | null;
  contactEmail: string | null;
  accountId: string | null;
  /**
   * Called after an instant action fires successfully (the sidebar has already
   * jumped to the run's Actions detail). Hosts whose own chrome would COVER
   * that detail — the search card popover — pass their dismiss here; the
   * contact-card header passes nothing.
   */
  onFired?: () => void;
  /**
   * Called after a successful enroll (the sidebar has already opened on the
   * Plays tab showing the new item). Same purpose as `onFired`: lets an
   * overlaying host get out of the way of the surface the action just opened.
   */
  onEnrolled?: () => void;
}

/**
 * "Actions ▾" — fires an instant global action (`POST
 * /api/v4/actions/:slug/execute`) with this contact's identity attached
 * (U3's `contact_id`/`contact_email`/`account_id` body fields). On success:
 * toast, an optimistic prepend into the Requests feed cache (U6's
 * `prependOptimisticRequest` helper — replaced by the real row once the
 * server's `execution_status_changed` SSE invalidate triggers a refetch),
 * and a jump straight into that execution's Requests-tab detail
 * (`openRequestDetail`, U6). On failure: an error toast and nothing is
 * prepended — there is no stale optimistic entry to clean up because the
 * prepend only happens after a confirmed success.
 *
 * R5 (QA round 2, recurring complaint): the confirmed-success feedback
 * (`openRequestDetail` + toast) must NEVER depend on the best-effort
 * optimistic-cache prepend succeeding. Order is deliberate:
 * `openRequestDetail` first, then the toast, then the prepend LAST inside
 * its own try/catch — a cache-write error (e.g. a dedup collapse) can only
 * ever cost the optimistic row, never the rep's confirmation that the fire
 * actually worked.
 */
function ActionsDropdown({ contactId, contactName, contactEmail, accountId, onFired }: ContactActionMenusProps) {
  // Geometry (incl. the "trigger scrolled fully out of view closes the menu
  // outright" fix #6 guard) lives in `usePortalDropdown` now — see its doc
  // comment (Unit 3 dropdown-positioning-logic promotion, D9).
  const { open, setOpen, triggerRef, menuRef, menuStyle } = usePortalDropdown<HTMLDivElement, HTMLDivElement>();
  const { readOnly } = useEnrollQueue();
  const { definitions, isLoading, available, unavailable, firingSlug, fire } = useFireInstantAction({
    contactId, contactName, contactEmail, accountId, onFired,
  });

  // `fire()` (useContactActions.ts) intentionally does not close this menu —
  // this component owns its own open state, so the click handler closes it
  // itself before delegating to the lifted fire logic.
  const handleFire = (def: InstantActionDefinition) => {
    setOpen(false);
    void fire(def);
  };

  const hasDefinitions = definitions && definitions.length > 0;

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={readOnly}
        title={readOnly ? READ_ONLY_TITLE('Actions') : 'Fire an instant action'}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="contact-actions-trigger"
        className={TRIGGER_CLASS}
      >
        <Zap className="w-3 h-3 text-lemon-600" />
        Actions
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid="contact-actions-dropdown"
          style={menuStyle}
          className="w-64 rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 shadow-lg overflow-hidden"
        >
          <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium">
            Instant actions
          </div>
          {isLoading ? (
            <div className="px-3 py-4 text-center text-[11px] text-content-tertiary dark:text-nightshade-500">
              Loading actions…
            </div>
          ) : !hasDefinitions ? (
            <div className="px-3 py-4 text-center text-[11px] text-content-tertiary dark:text-nightshade-500">
              No actions available
            </div>
          ) : (
            <>
              {available.length === 0 && (
                <div className="px-3 py-2 text-center text-[11px] text-content-tertiary dark:text-nightshade-500">
                  No actions available for this contact
                </div>
              )}
              {available.length > 0 && (
                <div className="py-1">
                  {available.map(def => (
                    <button
                      key={def.slug}
                      type="button"
                      role="menuitem"
                      disabled={firingSlug !== null}
                      onClick={() => handleFire(def)}
                      data-testid={`contact-actions-item-${def.slug}`}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-bg-secondary dark:hover:bg-nightshade-700 disabled:opacity-50 disabled:cursor-not-allowed text-content-primary dark:text-nightshade-100"
                    >
                      {firingSlug === def.slug ? (
                        <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                      ) : (
                        <Zap className="w-3 h-3 text-lemon-600 shrink-0" />
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium truncate">{def.name}</span>
                        {def.description && (
                          <span className="block text-[10.5px] text-content-tertiary dark:text-nightshade-500 truncate">
                            {def.description}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {unavailable.length > 0 && (
                <div className="px-2.5 py-1.5 border-t border-secondary dark:border-nightshade-700">
                  <Collapsible
                    trigger={
                      <span className="text-[10.5px] text-content-tertiary dark:text-nightshade-500">
                        {unavailable.length} unavailable
                      </span>
                    }
                    triggerAriaLabel={`${unavailable.length} unavailable action${unavailable.length === 1 ? '' : 's'} — expand for details`}
                    triggerTestId="contact-actions-unavailable-toggle"
                  >
                    <div className="space-y-1 pt-0.5">
                      {unavailable.map(({ def, missing }) => (
                        <div
                          key={def.slug}
                          data-testid={`contact-actions-unavailable-${def.slug}`}
                          className="px-0.5 text-[10.5px] text-content-tertiary dark:text-nightshade-500"
                        >
                          {def.name} — requires {missing.map(m => m.label).join(', ')}
                        </div>
                      ))}
                    </div>
                  </Collapsible>
                </div>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * "Queue for Play ▾" (U1 rename — was "Workflows ▾"; QA feedback found the
 * old label unclear about what it actually does) — enroll into a live play.
 * Reuses the exact rail every other enroll affordance uses
 * (`useEnrollQueue().plays`/`PlayPicker` for the catalog UI — including
 * `PlayPicker`'s own built-in search-to-filter input, which already covers
 * U1's "search within the dropdown" ask with no duplicated filtering logic
 * here; `addContact`/`assignPlay` to write) — no new enroll path. If the
 * contact already has a queue item (any status), the play is assigned onto
 * that existing item (`assignPlay`); otherwise a new item is added WITH the
 * play in one request (`addContact`'s optional `playTypeId`/
 * `playInstanceRef`, the same one-shot rail `StatePills.tsx`'s "Suggested
 * Invite" button uses).
 *
 * Q1 (QA round 3): when the contact is already in the queue (`existingItem`
 * below — the same `groups`-derived membership check the retired
 * `EnrollButton` used for its own queued chrome), the trigger renders a
 * distinct "✓ Queued" state instead of the dropdown trigger. Clicking it
 * jumps straight to the docked sidebar's Queue tab (`openQueueTab`) rather
 * than opening the plays picker — there's nothing left to pick, the contact
 * is already enrolled.
 *
 * Exported (flow-contacts-bulk-send U7, decision 12): this is the ONE
 * single-contact enroll component for all three surfaces — mounted here via
 * `ContactActionMenus` for the contact card and `SearchResultsSurface`, and
 * mounted directly (no `ActionsDropdown` alongside it) as `ContactFlowTable`'s
 * row action, which needs only the compact "Enroll ⌄" trigger, not the full
 * "Actions ▾" cluster. `EnrollButton.tsx`, the row's previous rowAction, is
 * deleted outright — this replaces it everywhere, not just here.
 */
export function WorkflowsDropdown({
  contactId, contactName, contactEmail, onEnrolled, iconOnly,
}: Pick<ContactActionMenusProps, 'contactId' | 'contactName' | 'contactEmail' | 'onEnrolled'> & {
  /** Renders just the state icon (Check when queued, Plus when not) instead
   *  of the full labeled button — for inline placement in a compact row
   *  header (e.g. ContactTimeline's action rows, 2026-09-23) alongside a
   *  link icon like `SourceLinkIcon`/`SfdcCloudIcon`. Same click behavior
   *  and dropdown either way. */
  iconOnly?: boolean;
}) {
  // Geometry (incl. the "trigger scrolled fully out of view closes the menu
  // outright" fix #6 guard) lives in `usePortalDropdown` now — see its doc
  // comment (Unit 3 dropdown-positioning-logic promotion, D9).
  // The picker's auto-email confirm portals to `document.body`, outside
  // `menuRef` — veto this hook's outside-click/Escape layers while it is open
  // (see `PlayPicker`'s `onAutoEmailConfirmOpenChange` doc). `canClose` is
  // re-read fresh on every dismiss attempt, so a plain state read is enough.
  const [autoEmailConfirmOpen, setAutoEmailConfirmOpen] = useState(false);
  const { open, setOpen, triggerRef, menuRef, menuStyle } = usePortalDropdown<HTMLDivElement, HTMLDivElement>({
    canClose: () => !autoEmailConfirmOpen,
  });
  const {
    queued, pending, catalogLoading, contactHasEmailForPicker, warmIntroAvailable,
    readOnly, plays, activePlayTypes, gongFlowsSuggested, enroll, addUnassigned, openQueueTab,
  } = useEnrollContact({ contactId, contactName, contactEmail, onEnrolled, catalogOpen: open });

  // `enroll()`/`addUnassigned()` (useContactActions.ts) intentionally do not
  // close this menu — this component owns its own open state, so the click
  // handlers close it themselves before delegating to the lifted logic.
  const handleEnroll = (play: Parameters<typeof enroll>[0]) => {
    setOpen(false);
    void enroll(play);
  };

  const handleAddUnassigned = () => {
    setOpen(false);
    void addUnassigned();
  };

  // Q1: already queued — swap the dropdown trigger for a queued affordance
  // that jumps to the sidebar instead of reopening the plays picker.
  if (queued) {
    if (iconOnly) {
      return (
        <button
          type="button"
          // Mounted inside clickable timeline rows (ContactTimeline/
          // AccountTimeline action rows) — must stop propagation so a click
          // here doesn't also toggle the parent row's expand/collapse. Safe
          // against usePortalDropdown's outside-click-close, which listens
          // on `document` in the capture phase (runs before this handler).
          onClick={e => { e.stopPropagation(); if (!readOnly) openQueueTab(); }}
          onKeyDown={e => e.stopPropagation()}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_TITLE('Queue for Play') : 'Already queued — open the queue'}
          data-testid="contact-workflows-trigger"
          className="text-content-success dark:text-emerald-400 hover:text-content-success dark:hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="w-[15px] h-[15px]" />
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); if (!readOnly) openQueueTab(); }}
        onKeyDown={e => e.stopPropagation()}
        disabled={readOnly}
        title={readOnly ? READ_ONLY_TITLE('Queue for Play') : 'Already queued — open the queue'}
        data-testid="contact-workflows-trigger"
        className={`${TRIGGER_CLASS} border-content-success text-content-success bg-bg-green-light dark:text-emerald-400 dark:border-emerald-700`}
      >
        <Check className="w-3 h-3" />
        Queued
      </button>
    );
  }

  return (
    <div className="relative" ref={triggerRef}>
      {iconOnly ? (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
          onKeyDown={e => e.stopPropagation()}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_TITLE('Queue for Play') : 'Add to queue'}
          aria-expanded={open}
          aria-haspopup="true"
          data-testid="contact-workflows-trigger"
          className="text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-[15px] h-[15px]" />
        </button>
      ) : (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
          onKeyDown={e => e.stopPropagation()}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_TITLE('Queue for Play') : 'Enroll into a play'}
          aria-expanded={open}
          aria-haspopup="true"
          data-testid="contact-workflows-trigger"
          className={TRIGGER_CLASS}
        >
          <Diamond className="w-3 h-3 text-blueberry-500" />
          Enroll
          <ChevronDown className="w-3 h-3" />
        </button>
      )}

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid="contact-workflows-dropdown"
          style={menuStyle}
          className="w-72"
        >
          <PlayPicker
            plays={plays}
            activePlayTypes={activePlayTypes}
            gongFlowsSuggested={gongFlowsSuggested}
            onAssign={play => handleEnroll(play)}
            onBrowseAll={() => handleAddUnassigned()}
            onAddUnassigned={() => handleAddUnassigned()}
            disabled={pending || catalogLoading || readOnly}
            disabledReason={catalogLoading ? 'Loading play catalog…' : readOnly ? READ_ONLY_TITLE('Queue for Play') : undefined}
            ariaLabelPrefix="Enroll in"
            contactHasEmail={contactHasEmailForPicker}
            warmIntroAvailable={warmIntroAvailable}
            contactLabel={contactName ?? undefined}
            onAutoEmailConfirmOpenChange={setAutoEmailConfirmOpen}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * The two dropdowns, side by side (prototype's `c-head .cluster`). `flex-wrap`
 * is a no-op in V1 (this cluster's parent panel is always ≥400px wide, and
 * the name block truncates before these triggers would need to wrap) — it
 * matters in the V2 ledger, where the expanded detail band is now pinned to
 * the visible scroll-corridor width (plan 2026-09-28-003 U1, item 1) and can
 * be narrower than both triggers combined.
 */
export function ContactActionMenus(props: ContactActionMenusProps) {
  // Positioning is handled per-menu by `usePortalDropdown` on a portaled
  // panel — no positioning context is needed on the cluster.
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ActionsDropdown {...props} />
      <WorkflowsDropdown contactId={props.contactId} contactName={props.contactName} contactEmail={props.contactEmail} onEnrolled={props.onEnrolled} />
    </div>
  );
}
